/**
 * Utility functions for generating and handling URL slugs
 */

/**
 * Generates a URL-friendly slug from a title string
 * @param {string} title - The title to convert to a slug
 * @returns {string} URL-friendly slug
 */
export const generateSlug = (title) => {
  if (!title) return "";
  return title
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "") // Remove special characters
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with single
    .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
};

/**
 * Generates a slug with ID suffix for uniqueness
 * @param {string} title - The course title
 * @param {string} id - The course ID (uuid)
 * @returns {string} Slug with short ID suffix for uniqueness
 */
export const generateSlugWithId = (title, id) => {
  const baseSlug = generateSlug(title);
  // Use first 8 chars of UUID for uniqueness
  const shortId = id ? id.substring(0, 8) : "";
  return shortId ? `${baseSlug}-${shortId}` : baseSlug;
};

/**
 * Extracts the ID from a slug that was generated with generateSlugWithId
 * @param {string} slug - The slug with ID suffix
 * @returns {string|null} The extracted ID prefix or null
 */
export const extractIdFromSlug = (slug) => {
  if (!slug) return null;
  // Get the last segment after the final hyphen (8 char ID)
  const parts = slug.split("-");
  const lastPart = parts.at(-1);
  // UUID segment is 8 hex chars
  if (lastPart && /^[a-f0-9]{8}$/i.test(lastPart)) {
    return lastPart;
  }
  return null;
};
